import { LeftOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Form } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  useCounterpartServiceCreateContractEndpoint,
  useCounterpartServiceGetContractEndpoint,
  useCounterpartServiceGetContractEndpointKey,
  useCounterpartServiceGetContractListEndpointKey,
  useCounterpartServiceGetCounterpartEndpoint,
  useCounterpartServiceUpdateContractEndpoint,
  useTemplatesServiceCreateSystemTemplateEndpoint
} from 'src/api/queries'
import { ApiError, CreateCounterpartContractProductDto } from 'src/api/requests'
import {
  FormFooter,
  LoadingScreen,
  message,
  PageBreadcrumbs
} from 'src/components'
import { useFileHandler } from 'src/hooks'
import { yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler, toDayjs } from 'src/utils'

import { AddContractForm, TemplateTitleModal } from '../components'

export type UploadResponse = {
  createdResources: { id: string }[]
}

type CustomCreateCounterpartContractProductDto =
  CreateCounterpartContractProductDto & {
    name: string
    measurementUnitName: string
    tvaUnitRateName: string
    group: string
    tvaTotal: number
    tvaUnitRateId: string
  }

export const AddContract = () => {
  const { t, currentLang } = useLocales()
  const navigate = useNavigate()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const { id, contractId } = useParams()

  const [form] = Form.useForm()

  const { setExistingFiles, onFileChange, saveFiles, isUploading } =
    useFileHandler({
      form,
      folder: `contracts-${id}`
    })

  const queryClient = useQueryClient()
  const onToggleModal = () => setIsModalOpen((prev) => !prev)

  const onMutationSuccess = () => {
    const queryKey = contractId
      ? [useCounterpartServiceGetContractEndpointKey, String(contractId)]
      : [useCounterpartServiceGetContractListEndpointKey]
    queryClient.invalidateQueries({ queryKey })
  }

  const { mutate } = useCounterpartServiceCreateContractEndpoint({
    onSuccess: () => {
      onMutationSuccess()
      message.success(t('counterparts.contact_created'))
      onBackClick()
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })

  const { mutate: createTemplate } =
    useTemplatesServiceCreateSystemTemplateEndpoint({
      onSuccess: () => {
        onMutationSuccess()
        message.success(t('counterparts.template_created'))
        onToggleModal()
      },
      onError: (error: ApiError) => formErrorHandler(error.body.errors)
    })

  const { mutate: edit } = useCounterpartServiceUpdateContractEndpoint({
    onSuccess: () => {
      onMutationSuccess()
      message.success(t('common.save_success'))
      onBackClick()
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })

  const { data: institution } = useCounterpartServiceGetCounterpartEndpoint(
    { id: String(id) },
    [String(id)],
    { enabled: !!id }
  )

  const { data, isLoading } = useCounterpartServiceGetContractEndpoint(
    {
      id: String(contractId)
    },
    [String(contractId)],
    {
      enabled: !!contractId
    }
  )

  const onBackClick = () => {
    form.resetFields()
    navigate(paths.counterpart.details(id))
  }

  const onSubmit = async ({
    createNewContract
  }: {
    createNewContract: boolean
  }) => {
    const values = await form.validateFields()

    const updatedFiles = await saveFiles()

    const items =
      values?.medicine?.map(
        (item: CustomCreateCounterpartContractProductDto) => ({
          stockProductId: item?.stockProductId,
          unitPrice: item?.unitPrice,
          measurementUnitId: item?.measurementUnitId,
          tvaUnitRateId: item?.tvaUnitRateId,
          tvaTotal: item?.tvaTotal,
          quantity: item?.quantity,
          stockProductName: item?.name,
          measurementUnitName: item?.measurementUnitName,
          tvaUnitRateName: item?.tvaUnitRateName,
          group: item?.group
        })
      ) ?? []

    const consumables =
      values?.consumables?.map(
        (item: CustomCreateCounterpartContractProductDto) => ({
          stockProductId: item?.stockProductId,
          unitPrice: item?.unitPrice,
          measurementUnitId: item?.measurementUnitId,
          tvaUnitRateId: item?.tvaUnitRateId,
          tvaTotal: item?.tvaTotal,
          quantity: item?.quantity,
          stockProductName: item?.name,
          measurementUnitName: item?.measurementUnitName,
          tvaUnitRateName: item?.tvaUnitRateName,
          group: item?.group
        })
      ) ?? []

    const payload = {
      beneficiaryId:
        data?.beneficiary?.name === values?.beneficiaryName
          ? data?.beneficiary?.id
          : values?.beneficiaryId,
      counterpartId: String(id),
      contractNumber: values.contractNumber,
      signedOn: dayjs(values.signedOn).format(yearMonthDaySlash),
      startDate: dayjs(values.startDate).format(yearMonthDaySlash),
      endDate: dayjs(values.endDate).format(yearMonthDaySlash),
      description: values.description,
      mediaFilesIds: updatedFiles,
      products: createNewContract ? [...items, ...consumables] : items,
      fundingSourceId: values?.fundingSourceId
    }

    if (createNewContract) {
      mutate({
        requestBody: payload
      })
    } else {
      contractId
        ? edit({
            id: String(contractId),
            requestBody: payload
          })
        : createTemplate({
            requestBody: {
              name: values.template_name,
              isPublic: true,
              description: values.template_description,
              content: payload
            }
          })
    }
  }

  useEffect(() => {
    if (contractId && data) {
      const existingFileIds = data?.files
        ?.map((file) => file?.id)
        .filter(Boolean) as string[]

      setExistingFiles(existingFileIds)

      form.setFieldsValue({
        beneficiaryId: data?.beneficiary?.name,
        contractNumber: data?.contractNumber,
        description: data?.description,
        signedOn: toDayjs(data?.signedOn),
        startDate: toDayjs(data?.startDate),
        endDate: toDayjs(data?.endDate),
        fundingSourceId: data?.fundingSourceId,
        medicine: data?.products?.map((item) => ({
          name: `${item?.stockProduct?.commercialName} ${item?.stockProduct?.doses ?? ''}`,
          producerName: item?.stockProduct?.producer?.name,
          group: item?.stockProduct?.group?.category,
          stockProductId: item?.stockProduct?.id,
          unitPrice: (item?.unitPrice ?? 0).toFixed(2),
          priceTotal: (
            (item?.quantity ?? 0) *
            (item?.unitPrice ?? 0) *
            (1 +
              parseFloat(
                item?.tvaUnitRate?.name?.[currentLang?.value]?.replace(
                  '%',
                  ''
                ) ?? '0'
              ) /
                100)
          ).toFixed(2),
          delivered_quantity:
            (item?.quantity ?? 0) - (item?.currentQuantity ?? 0),
          outstanding_quantity: item?.currentQuantity ?? 0,
          measurementUnitId: item?.measurementUnit?.id,
          measurementUnitName:
            item?.measurementUnit?.name?.[currentLang?.value],
          tvaTotal: item?.tvaTotal,
          tvaUnitRateName: item?.tvaUnitRate?.name?.[currentLang?.value],
          tvaUnitRateId: item?.tvaUnitRate?.id,
          quantity: item?.quantity
        })),
        consumables: data?.pharmaceutics?.map((item) => ({
          name: `${item?.stockProduct?.commercialName} ${item?.stockProduct?.doses ?? ''}`,
          producerName: item?.stockProduct?.producer?.name,
          group: item?.stockProduct?.group?.category,
          stockProductId: item?.stockProduct?.id,
          unitPrice: (item?.unitPrice ?? 0).toFixed(2),
          priceTotal: (
            (item?.quantity ?? 0) *
            (item?.unitPrice ?? 0) *
            (1 +
              parseFloat(
                item?.tvaUnitRate?.name?.[currentLang?.value]?.replace(
                  '%',
                  ''
                ) ?? '0'
              ) /
                100)
          ).toFixed(2),
          delivered_quantity:
            (item?.quantity ?? 0) - (item?.currentQuantity ?? 0),
          outstanding_quantity: item?.currentQuantity ?? 0,
          measurementUnitId: item?.measurementUnit?.id,
          measurementUnitName:
            item?.measurementUnit?.name?.[currentLang?.value],
          tvaTotal: item?.tvaTotal,
          tvaUnitRateName: item?.tvaUnitRate?.name?.[currentLang?.value],
          tvaUnitRateId: item?.tvaUnitRate?.id,
          quantity: item?.quantity
        })),
        docs: existingFileIds?.map((id) => ({
          uid: id,
          name: `File-${id}`,
          status: 'done'
        }))
      })
    }
  }, [data, contractId])

  if (isLoading) {
    return <LoadingScreen />
  }

  return (
    <PageBreadcrumbs
      title={
        contractId
          ? String(data?.contractNumber)
          : t('counterparts.add_new_contract')
      }
      items={[
        {
          title: t('counterparts.title'),
          path: paths.counterpart.root
        },
        ...(institution
          ? [
              {
                title: institution?.name ?? '-',
                path: paths.counterpart.details(id)
              }
            ]
          : []),
        {
          title: contractId
            ? String(data?.contractNumber)
            : t('counterparts.add_new_contract')
        }
      ]}
      extra={
        !contractId && (
          <Button
            type="primary"
            onClick={async () => {
              await form.validateFields()
              onToggleModal()
            }}
          >
            {t('counterparts.add_new_template_contract')}
          </Button>
        )
      }
    >
      <Flex vertical gap={24}>
        {contractId && (
          <Button
            icon={<LeftOutlined />}
            className="fit-content"
            onClick={onBackClick}
          >
            {t('common.back')}
          </Button>
        )}
        <Form layout="vertical" form={form} requiredMark={false}>
          <AddContractForm form={form} data={data} onChange={onFileChange} />

          {isModalOpen && (
            <TemplateTitleModal
              onCancel={onToggleModal}
              onOk={() => onSubmit({ createNewContract: false })}
            />
          )}

          <FormFooter
            onCancel={onBackClick}
            onSave={() => onSubmit({ createNewContract: true })}
            saveConfirmTitle={t('common.save')}
            saveLabel={t('common.save')}
            loading={isLoading || isUploading}
          />
        </Form>
      </Flex>
    </PageBreadcrumbs>
  )
}
